import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 2rem;
  min-height: 60vh;
`;

const BackButton = styled.button`
  background: transparent;
  color: #cc0000;
  border: 2px solid #cc0000;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #cc0000;
    color: white;
  }
`;

const SongTitle = styled.h1`
  color: #ffffff;
  font-size: 2.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  text-align: center;
`;

const ControlsContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  color: #ffffff;
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const AmountDisplay = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const AmountControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin: 1rem 0;
`;

const AmountButton = styled.button`
  background: ${props => props.primary ? '#cc0000' : '#444'};
  color: white;
  border: none;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => props.primary ? '#aa0000' : '#555'};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const ViewPDFButton = styled.button`
  background: #0066cc;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 1rem;
  
  &:hover {
    background: #0052a3;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const Checkbox = styled.input`
  width: 24px;
  height: 24px;
  cursor: pointer;
  accent-color: #cc0000;
`;

const CheckboxLabel = styled.label`
  color: #cccccc;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #cc0000;
`;

function ExternalSongPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadSong();
  }, [id]);

  const loadSong = async () => {
    try {
      setLoading(true);
      const songData = await songAPI.getSong(id);
      setSong(songData);
    } catch (error) {
      console.error('Error loading song:', error);
      toast.error('Failed to load song');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDF = async () => {
    try {
      const pdfBlob = await songAPI.getPDF(id);
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to view PDF. The file may be missing. Please try re-uploading it.';
      toast.error(errorMessage);
    }
  };

  const handleToggleActive = async (e) => {
    const newStatus = e.target.checked ? 'active' : 'archived';
    try {
      setUpdating(true);
      await songAPI.updateSong(id, { status: newStatus });
      setSong({ ...song, status: newStatus });
      toast.success(`Song ${newStatus === 'active' ? 'activated' : 'archived'}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
      e.target.checked = !e.target.checked; // Revert checkbox
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (!song) {
    return (
      <PageContainer>
        <ErrorMessage>
          <h2>Song not found</h2>
          <p>The song you're looking for doesn't exist.</p>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/external')}>
        ← Back to Songs
      </BackButton>

      <SongTitle>{song.name}</SongTitle>

      <ControlsContainer>
        <Section>
          <SectionTitle>Current Amount</SectionTitle>
          <AmountDisplay>{song.current_amount}</AmountDisplay>
          <CheckboxContainer style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
            <Checkbox
              type="checkbox"
              id="active"
              checked={song.status === 'active'}
              onChange={handleToggleActive}
              disabled={updating}
            />
            <CheckboxLabel htmlFor="active">Active</CheckboxLabel>
          </CheckboxContainer>
        </Section>

        <Section>
          <ViewPDFButton 
            onClick={handleViewPDF}
            disabled={!song.pdf_filename}
          >
            {song.pdf_filename ? '📄 View PDF' : 'No PDF Available'}
          </ViewPDFButton>
        </Section>
      </ControlsContainer>
    </PageContainer>
  );
}

export default ExternalSongPage;

